import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Hero} from '@/components/common/hero/Hero'
import {getBlogContent} from '@/content/blog/blog'
import {useTranslation} from '@/i18n'
import {BLOG_CONTENT_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Author, Blog} from '@/utils/contentful'
import {formatDate} from '@/utils/formatDate'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type BlogHeroProps = {
  blog: Blog
  breadcrumbTrail: BreadcrumbStep[]
}

export const BlogHero: FC<React.PropsWithChildren<BlogHeroProps>> = ({blog, breadcrumbTrail}) => {
  const language = useLanguage()
  const {t} = useTranslation('page')

  const {blogByTitle} = getBlogContent()

  return (
    <>
      <Hero
        title={blog.title}
        subtitle={blog.blogIntro}
        maxWidth={BLOG_CONTENT_WIDTH}
        breadcrumbs={
          <Breadcrumbs
            trail={breadcrumbTrail}
            // The article's own title is the h1 directly below.
            hideCurrent
          />
        }
        metaInfo={
          <div className="meta">
            <Text variant={TextVariant.text14} fontWeight={TextFontWeight.bold}>
              <Text variant={TextVariant.text14} element="span" fontWeight={TextFontWeight.regular}>
                {t(blogByTitle)}{' '}
              </Text>
              {blog.authors.map((author: Author) => author.name).join(', ')}
            </Text>
            <div className="meta-data">
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                <span className="meta-info">{formatDate(blog.date, language, true)}</span>
                <span className="meta-info">{blog.readingTime}</span>
              </Text>
            </div>
          </div>
        }
        justifyContent="center"
      />

      <style jsx>{`
        .meta {
          display: flex;
          margin-top: ${theme.spacing[5]};
        }
        .meta-data {
          display: flex;
          align-items: center;
        }
        .meta-data::before {
          content: '';
          width: 1px;
          height: 16px;
          background-color: ${theme.palette.gray[400]};
          margin: 0 ${theme.spacing[2.5]};
        }
        .meta-info {
          position: relative;
        }
        .meta-info + .meta-info {
          padding-left: ${theme.spacing[3]};
          display: inline-flex;
          align-items: center;
        }
        .meta-info + .meta-info:before {
          content: '';
          position: absolute;
          left: 6px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: ${theme.palette.text.light};
        }
        @media ${MOBILE_SCREENSIZE} {
          .meta {
            flex-direction: column;
            gap: ${theme.spacing[1]};
          }
          .meta-data::before {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
